import { FC } from 'react';
import {
  alpha,
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ImageNotSupported,
  Settings as SettingsIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import type { CatalogItem, Extra, Specification, Trim } from '../../types';

interface CatalogItemCardProps {
  item: CatalogItem;
  onEdit: (item: CatalogItem) => void;
  onDelete: (id: string) => void;
  onManageSpecs?: (item: CatalogItem) => void;
  showPrimalBadge?: boolean;
  primalName?: string;
  hideImage?: boolean;
}

export const CatalogItemCard: FC<CatalogItemCardProps> = ({
  item,
  onEdit,
  onDelete,
  onManageSpecs = undefined,
  showPrimalBadge = false,
  primalName = undefined,
  hideImage = false,
}) => {
  const isPrimal = item.type === 'primal';
  const hasImage =
    !hideImage && !isPrimal && 'imageUrl' in item && !!item.imageUrl;
  const hasSpecs =
    !isPrimal &&
    'specifications' in item &&
    item.specifications &&
    item.specifications.length > 0;

  const hasNoOptionsWarning =
    item.type === 'specification' &&
    ((item as Specification).inputType === 'select' ||
      (item as Specification).inputType === 'multiselect') &&
    (!(item as Specification).options ||
      (item as Specification).options!.length === 0);

  const getTypeColor = () => {
    switch (item.type) {
      case 'primal':
        return 'primary';
      case 'cut':
        return 'secondary';
      case 'trim':
        return 'info';
      case 'extra':
        return 'success';
      case 'specification':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTypeLabel = () => {
    if (item.type === 'specification') return 'Specification';
    return item.type.charAt(0).toUpperCase() + item.type.slice(1);
  };

  const description =
    !isPrimal && 'description' in item
      ? (item as { description?: string }).description
      : undefined;

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderWidth: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          minHeight: { xs: 100, md: 120 },
        }}
      >
        {/* Image on the left */}
        {hasImage && 'imageUrl' in item && item.imageUrl && (
          <Box
            sx={{
              width: { xs: 100, md: 120 },
              minWidth: { xs: 100, md: 120 },
              alignSelf: 'stretch',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={item.imageUrl}
              alt={item.title || ''}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        {!hasImage && !hideImage && (
          <Box
            sx={{
              width: { xs: 100, md: 120 },
              minWidth: { xs: 100, md: 120 },
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (theme) => alpha(theme.palette.info.light, 0.15),
            }}
          >
            <ImageNotSupported color="disabled" />
          </Box>
        )}

        {/* Content area */}
        <Stack
          sx={{ flex: 1, minWidth: 0, p: 2 }}
          justifyContent="center"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" noWrap title={item.title}>
              {item.title}
            </Typography>

            {/* Action buttons */}
            <Stack direction="row" spacing={0} sx={{ ml: 1, flexShrink: 0 }}>
              {onManageSpecs && (
                <IconButton
                  size="small"
                  onClick={() => onManageSpecs(item)}
                  title="Manage Specifications"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => onEdit(item)}
                title="Edit"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onDelete(item.id)}
                title="Delete"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {description}
            </Typography>
          )}

          {/* Metadata badges */}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
          >
            {item.type === 'specification' && (
              <Chip
                label={getTypeLabel()}
                color={getTypeColor()}
                size="small"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
            {item.type === 'trim' && (item as Trim).isDefault && (
              <Chip
                label="Default"
                color="warning"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
            {item.type === 'extra' && (item as Extra).hasCharge && (
              <Chip
                label={`$${(item as Extra).price || 0}`}
                color="success"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
            {hasSpecs && 'specifications' in item && item.specifications && (
              <Typography variant="caption" color="text.secondary">
                {item.specifications.length} spec
                {item.specifications.length !== 1 ? 's' : ''}
              </Typography>
            )}
            {hasNoOptionsWarning && (
              <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
